/**
 * Swamithra — the user's vetting requests (US-00-004).
 *
 * A notice and a draft go up together and come back with one expert review. As
 * with queries there is NO update and NO delete: US-00-004.12 requires that any
 * attempt to change the title, category, notes or either upload be rejected and
 * the stored record left alone, so no such function exists here.
 */

import { referenceParam } from "../domain/ids"
import { type PageParams, pageQuery, paginated } from "../domain/pagination"
import {
	type CreateVettingInput,
	createVettingInputSchema,
	type MyVettingFilters,
	type PostVettingMessageInput,
	postVettingMessageInputSchema,
	vettingDetailSchema,
	type VettingFeedbackInput,
	vettingFeedbackInputSchema,
	vettingFeedbackSchema,
	vettingMessageSchema,
	vettingSummarySchema,
} from "../domain/vetting"
import { appendFiles, type PlatformFile } from "../files"
import { api } from "./client"
import { type ReadOptions, type SubmitOptions } from "./options"

const myVettingPage = paginated(vettingSummarySchema)

export function fetchMyVettingRequests(
	filters: MyVettingFilters = {},
	page: PageParams = {},
	options: ReadOptions = {},
) {
	return api.get("/vetting-requests", myVettingPage, {
		...options,
		query: { ...pageQuery(page), ...(filters.status ? { status: filters.status } : {}) },
	})
}

/**
 * One vetting request.
 *
 * `vettingRef` is the human-readable number — `V12`, or `#V12` — which is what
 * `/vetting-requests/{vettingRef}` takes. Another user's, including a guessed
 * one, is 403 with no field of the record in the body (US-00-004.16).
 */
export function fetchVettingRequest(vettingRef: string, options: ReadOptions = {}) {
	return api.get(`/vetting-requests/${referenceParam(vettingRef)}`, vettingDetailSchema, options)
}

/**
 * Creates a vetting request.
 *
 * BOTH FILES ARE REQUIRED and they are separate fields, not an array: they have
 * different accepted types — the notice takes PDF/JPG/PNG, the draft
 * additionally takes DOCX (US-00-004.3) — and the form has to say which upload
 * is missing when one of them is (US-00-004.2). An array of two would lose that.
 */
export async function createVettingRequest(
	input: CreateVettingInput,
	files: { notice: PlatformFile; draft: PlatformFile },
	options: SubmitOptions,
) {
	const parsed = createVettingInputSchema.parse(input)
	const form = new FormData()
	form.append("title", parsed.title)
	if (parsed.notes !== undefined) form.append("notes", parsed.notes)
	appendFiles(form, "notice", [files.notice])
	appendFiles(form, "draft", [files.draft])

	return await api.post("/vetting-requests", vettingDetailSchema, form, {
		signal: options.signal,
		idempotencyKey: options.idempotencyKey,
	})
}

/**
 * Posts a follow-up into the thread.
 *
 * Plain text, JSON — there is no file-upload control on a vetting message
 * (unlike a query follow-up, which is multipart). A follow-up on an Answered
 * request reopens it to In Progress; the taxpayer keeps talking and the
 * Expert sees it needs another look. Mirrors postFollowUp for queries.
 */
export async function postVettingMessage(
	vettingRef: string,
	input: PostVettingMessageInput,
	attachments: readonly PlatformFile[],
	options: SubmitOptions,
) {
	const parsed = postVettingMessageInputSchema.parse(input)
	const form = new FormData()
	form.append("body", parsed.body)
	appendFiles(form, "attachments", attachments)
	return await api.post(
		`/vetting-requests/${referenceParam(vettingRef)}/messages`,
		vettingMessageSchema,
		form,
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/**
 * 'Was this helpful?' on a reviewed vetting request.
 *
 * The response is the feedback, not the request — 201 with the rating, the
 * comment and when it was recorded. Submitting also CLOSES the vetting request
 * in the same transaction on the server, so the caller has to refetch the
 * detail rather than read a new status out of this; the hook that owns the
 * cache does exactly that. A second submission returns 409 and changes
 * nothing. Mirrors submitFeedback for queries.
 */
export async function submitVettingFeedback(
	vettingRef: string,
	input: VettingFeedbackInput,
	options: SubmitOptions,
) {
	return await api.post(
		`/vetting-requests/${referenceParam(vettingRef)}/feedback`,
		vettingFeedbackSchema,
		vettingFeedbackInputSchema.parse(input),
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}
